import {usePermissions} from '@/features/auth/store/store'
import {useGetMedicalReportTypes} from '@/features/medical-reports/queries/useGetMedicalReportTypes'
import {generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {PatientExtended} from '@/features/patients/types'
import {Trans, useTranslation} from '@/translations/i18n'
import {useNavigate} from 'react-router-dom'
import {StyledProjectWarningMissingBanner} from '../style'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Button} from '@/components/ui/button/Button'
import {CreateMedicalRecordButton} from '@/features/medical-records/components/create-medical-record-button/CreateMedicalRecordButton'
import {QUERY_KEYS, queryClient} from '@/queryClient'

export const ProjectWarningMissingRenewalReport = ({
    projectId,
    patient,
    expirationDate
}: {
    projectId: string
    patient: PatientExtended
    expirationDate: Date
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const permissions = usePermissions()
    // TODO: this should be emproved by adding a new endpoint to get the medical report type by project type or something else
    const {data: medicalReportTypes, isLoading} = useGetMedicalReportTypes({
        search: 'Rinnovo progetto continuativo'
    })

    const medicalReportType = medicalReportTypes?.[0]?.types.find(type => type.keyName == 'renew_continuous_project')
    const hasMedicalRecord = !!patient.medicalRecord

    const handleNavigateToMedicalRecord = () => {
        if (!medicalReportType) return

        const path = generateCreateMedicalReportPath(
            patient.id,
            projectId,
            {
                id: medicalReportType.id,
                name: medicalReportType.name,
                keyName: medicalReportType.keyName,
                groupName: medicalReportType.groupName,
                groupKeyName: medicalReportType.groupKeyName
            },
            null,
            expirationDate
        )
        if (path) {
            navigate(path)
        }
    }

    return (
        <StyledProjectWarningMissingBanner align="center">
            <Flexbox gap={1} align="center">
                <AlertCircleIcon />
                <Text palette="neutral" shade="950" size="textSm">
                    <Trans
                        i18nKey="projects:missing_relation_content"
                        components={{b: <b />}}
                        values={{
                            relation: t('medical_records:type_renew_continuous_project')
                        }}
                    />
                </Text>
                {permissions?.medicalRecords.write && (
                    <>
                        {isLoading ? (
                            <Spinner size={16} />
                        ) : hasMedicalRecord ? (
                            <Button onClick={handleNavigateToMedicalRecord} variant="tertiary" size="sm">
                                {t('commons:add')}
                            </Button>
                        ) : (
                            <CreateMedicalRecordButton
                                patient={patient}
                                navigateToCreateReport={true}
                                variant="tertiary"
                                size="sm"
                                hideIcon
                                onSuccess={() => {
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                                    void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.AGENDA_EVENTS]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                                    void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
                                }}
                                searchParams={{
                                    projectId,
                                    medicalReportType
                                }}
                            >
                                {t('commons:add')}
                            </CreateMedicalRecordButton>
                        )}
                    </>
                )}
            </Flexbox>
        </StyledProjectWarningMissingBanner>
    )
}
